import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { OrganizationSetupFormService } from '../../contract-management/services/organization-setup-from.service';
import { IOrganization, IUser, IWorkspace, SHARE_FEATURE } from '../models/model';
import { BaseService } from './base.service';
import { LOGIN_WITH } from '../models/enums';
import { SubscriptionStateService } from './subscription-state.service';
import { SocketService } from './socket.service';


export enum PRIMARY_AUTH_METHOD {
    'PASSKEYS' = 'PASSKEYS',
    'PASSWORD' = 'PASSWORD',
    'EMAIL_OTP' = 'EMAIL_OTP',
    'SMS_OTP' = 'SMS_OTP',
    'MFA' = 'MFA',
}

export enum EMAIL_TYPE {
    'PERSONAL' = 'PERSONAL',
    'WORK' = 'WORK',
    'INVALID' = 'INVALID',
    'DUPLICATE' = 'DUPLICATE',
}

export interface ILogin {
    token: string,
    email?: string,
    primaryAuthMethod: PRIMARY_AUTH_METHOD,
    refreshToken: string,
    passkeyRequired?: boolean,
    passkeyRegistrationRequired?: boolean,
    mfa_required?: boolean,
    mfa_registration_required?: boolean,
    mfa_data?: {
        secret: string,
        qrCode: string
    }

}

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseService {

    apiUrl = environment.apiUrl + '/authentication';
    token = localStorage.getItem('token')
    public currentUserSubject = new BehaviorSubject<any>(this.token);
    public currentUser: Observable<IUser | undefined | null>;
    private tokenExpiredSubject: BehaviorSubject<boolean>;
    public tokenExpired!: Observable<boolean>;

    private subscriptionStateService = inject(SubscriptionStateService);
    private injector = inject(Injector);

    constructor(
        protected override http: HttpClient,
        private router: Router,
        private organizationSetupFormService: OrganizationSetupFormService
    ) {
        super(http);
        this.currentUserSubject = new BehaviorSubject<IUser | undefined | null>(this.getUser());
        this.currentUser = this.currentUserSubject.asObservable();
        this.tokenExpiredSubject = new BehaviorSubject<boolean>(this.isRefreshTokenExpired());
        this.tokenExpired = this.tokenExpiredSubject.asObservable();
    }


    public login(data: { loginWith: LOGIN_WITH, identifier: string, password: string }) {
        return this.http.post<any>(this.apiUrl + '/login', data).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    /**
     * Sets user data after successful authentication.
     * 
     * Note: Subscription fetching is now handled in AppComponent for both login and reload scenarios.
     * 
     * @param data - Login response data containing token and auth method
     * @returns User object or login data
     */
    setUserData(data: ILogin | Partial<ILogin> | null | undefined) {
        if (!data) {
            return null;
        }
        let user = null;
        // login successful if there's a jwt token in the response
        if (data.token) {
            user = this.getPayLoad(data.token);
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('token', data.token);
            localStorage.setItem('refreshToken', data.token);
            this.currentUserSubject.next(user);
            this.tokenExpiredSubject.next(false);
            this.organizationSetupFormService.setCurrentWorkspace(user.currentWorkspace?._id ? user.currentWorkspace : { _id: user.currentWorkspace } as IWorkspace);
        }

        if (data.primaryAuthMethod != PRIMARY_AUTH_METHOD.PASSWORD) {
            return data;
        }
        return user;
    }

    public getPayLoad(token: string) {
        const decoded = jwtDecode(token) as any;
        return decoded;
    }

    logout(needRedirect: boolean = true) {
        const user = this.currentUserSubject.value;
        if (user) {
            this.injector.get(SocketService).leaveAllRooms(user.organization as string ?? '', user._id as string ?? '');
        }
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        this.organizationSetupFormService.clearCurrentWorkspace();
        this.subscriptionStateService.clearSubscriptionDetails();
        this.currentUserSubject.next(null)
        if (needRedirect) {
            this.router.navigate(['/login']);
        }
    }

    signUp(data: FormData) {
        return this.post<any>(this.apiUrl + '/sign-up', data).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    sendOTP(email: string) {
        return this.post(this.apiUrl + '/send-otp', { email })
    }

    sendTwoFactorEmailOtp(email: string) {
        return this.post(this.apiUrl + '/send-two-factor-email-otp', { email })
    }

    sendUnusualActvityEmail(email: string, name: string, timestamp: string) {
        return this.post(this.apiUrl + '/send-email-for-exceeded-otp-attempt', { email, name, timestamp })
    }

    verifyTwoFactorOTP(email: string, otp: string) {
        return this.post<{
            token: string,
            status: boolean,
        }>(this.apiUrl + '/verify-two-factor-email-otp', { email, otp }).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    checkOrganization(name: string) {
        return this.post(this.apiUrl + '/check-organization', { name })
    }

    checkDomain(domain: string) {
        return this.post(this.apiUrl + '/check-domain', { domain })
    }

    verifyIdentifierAndGetLoginMethods(identifier: string, loginWith: LOGIN_WITH) {
        return this.post<{ loginMethods: string[], userExists: boolean, userDeactivated: boolean, email?: string }>(this.apiUrl + '/verify-identifier-and-get-login-methods', { identifier, loginWith })
    }

    verifyOTP(email: string, otp: string) {
        return this.post(this.apiUrl + '/verify-otp', { email, otp })
    }
    sendVerificationEmail(email: string) {
        return this.post(this.apiUrl + '/forgot-password', { email })
    }

    resetPassword(userId: string, token: string, newPassword: string) {
        return this.post(this.apiUrl + '/reset-password', { userId, token, newPassword })
    }

    validateToken(token: string, userId: string) {
        return this.post(this.apiUrl + '/validate-token', { token, userId })
    }

    public getUser(): IUser | undefined {
        const token = localStorage.getItem('token');
        if (token) {
            const user = this.getPayLoad(token);
            return user;
        }
        return undefined;
    }

    isRefreshTokenExpired(token?: string): boolean {
        // for refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            return true;
        }
        return false;
    }

    userSignUpFromInvitation(data: FormData) {
        return this.post<ILogin>(this.apiUrl + '/invitation-signup', data);
    }

    checkForOtp(email: string) {
        return this.post(this.apiUrl + '/check-for-otp', { email })
    }

    // Get anonymous ID, ensuring synchronous flow
    async generateAnonymousId(id: string, shareUserId: string | null, contractId: string, feature?: SHARE_FEATURE): Promise<any> {
        const data = await firstValueFrom(this.post<ILogin>(`${this.apiUrl}/anonymous-token`, {
            id,
            shareUserId,
            contractId,
            ...(feature ? { feature } : {})
        }));
        return this.setUserData(data.data);
    }

    checkUsername(username: string) {
        return this.get(this.apiUrl + '/check-username/' + username);
    }

    updateEmail(updateEmailToken: string) {
        return this.post(this.apiUrl + '/update-email', { updateEmailToken })
    }

    checkForMfa(email: string) {
        return this.post(this.apiUrl + '/check-for-mfa', { email })
    }

    completeMFARegistration(email: string, secret: string) {
        return this.post(this.apiUrl + '/register-mfa', { email, secret })
    }

    verifyMFA(email: string, mfaCode: string) {
        return this.post<any>(this.apiUrl + '/verify-mfa', { email, mfaCode }).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    webAuthnRegisterOptions(email: string) {
        return this.post<any>(this.apiUrl + '/webauthn-register-options', { email })
    }

    registerPasskey(email: string, passkey: any) {
        return this.post<any>(this.apiUrl + '/webauthn-register', { email, passkey })
    }

    webAuthnOptions(email: string) {
        return this.post<any>(this.apiUrl + '/webauthn-auth-options', { email });
    }

    webAuthnPasskeyAuthenticate(email: string, passkey: any) {
        return this.post<any>(this.apiUrl + '/webauthn-authenticate', { email, passkey }).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    googleAuthUrl(email?: string) {
        return this.get<any>(this.apiUrl + '/google-auth-url', email ? { email } : undefined)
    }

    googleAuthConfirmation(code: string, email?: string) {
        return this.post<ILogin>(this.apiUrl + '/google-auth', { code, email }).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    microsoftAuthUrl(email?: string) {
        return this.get<any>(this.apiUrl + '/microsoft-auth-url', email ? { email } : undefined)
    }

    microsoftAuthConfirmation(code: string, email?: string) {
        return this.post<ILogin>(this.apiUrl + '/microsoft-auth', { code, email }).pipe(map(data => {
            return this.setUserData(data.data)
        }))
    }

    getAvailableMFAMethods(email: string) {
        return this.get<any>(this.apiUrl + '/available-mfa-methods', { email })
    }

    verifyEmailForSignUp(email: string) {
        return this.post<{ emailType: EMAIL_TYPE, domain: string, organizationWithSameDomain: IOrganization | null }>(this.apiUrl + '/verify-email-for-signup', { email })
    }

    requestToJoinOrganization(email: string, organizationId: string) {
        return this.post<any>(this.apiUrl + '/request-to-join-organization', { email, organizationId });
    }

    checkEmails(emails: string[]) {
        return this.post<Array<{
            email: string,
            exists: boolean,
            message: string
        }>>(this.apiUrl + `/check-emails`, { emails })
    }

}
